/* Time, for the whole app — one module, both sides.
 *
 * This file exists because a shop that reads a date two different ways in two
 * different screens stops trusting either of them. Every day arithmetic, every
 * comparison and every rendered date in the client and the server goes through
 * something here.
 *
 * **There are two kinds of time in this app, and keeping them apart is the
 * whole idea.**
 *
 *   A **day** is a square on a calendar — "2026-09-11". The shop promises work
 *   on Friday, takes goods in on Tuesday, and holds a deadline on the 20th.
 *   None of those is a moment: they have no hour, and so they have no timezone
 *   either. Days are `@db.Date` columns, `IsoDay` strings on the wire, and they
 *   compare and sort correctly as plain strings.
 *
 *   An **instant** is a point on the clock — when a notice went up, when
 *   somebody last signed in, when a session expires. Those are `timestamptz(3)`
 *   columns, stored in UTC, ISO 8601 on the wire, and rendered in the shop's
 *   own timezone.
 *
 * Confusing the two is the bug this file is written to prevent. Store a
 * promised *day* as an instant and Brisbane reads the day before it for the
 * first ten hours of every working day, because UTC is still on yesterday —
 * which is exactly the complaint a job board cannot survive. Store an instant
 * as a day and you have thrown away the hour it happened at.
 *
 * The **timezone** is a column on the organisation (`IDEA.md` §8), so the shop
 * says what "today" means rather than the server's container or the iPad's
 * clock. It matters in exactly two places — what today is, and how an instant
 * reads — so those are the only functions here that take one.
 *
 * Nothing here uses the host's local timezone, ever. Every `Date` built inside
 * this file is pinned to UTC midnight and used as a calendar, never as a clock. */
/** What a shop starts as. The client is a Brisbane spray shop; a shop in
 *  another state changes it in Settings → Organisation. */
export const DEFAULT_TIMEZONE = 'Australia/Brisbane';
/** The zones the picker puts at the top, because they are the ones this shop
 *  and anyone like it will actually pick. Everything IANA knows is still in
 *  the list underneath — see `allTimezones`. */
export const COMMON_TIMEZONES = [
    'Australia/Brisbane',
    'Australia/Sydney',
    'Australia/Melbourne',
    'Australia/Adelaide',
    'Australia/Perth',
    'Australia/Darwin',
    'Australia/Hobart',
    'Pacific/Auckland',
];
/** Is this a timezone this runtime knows?
 *
 *  Asked of `Intl` rather than of a list kept in the repo: the IANA database
 *  changes when governments change their minds about daylight saving, and a
 *  hand-kept list is a list that is wrong the first time that happens. The
 *  validator and the picker both ask this, so they cannot disagree. */
export function isTimezone(value) {
    if (!value)
        return false;
    try {
        // Throws RangeError for anything that is not a zone this runtime has.
        new Intl.DateTimeFormat('en-AU', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
/** Every zone this runtime knows, the common ones first and each of them only
 *  once. `Intl.supportedValuesOf` is recent enough to be worth a fallback —
 *  where it is missing the picker still offers the list above, which covers
 *  every shop this app has. */
export function allTimezones() {
    const supported = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
    const common = COMMON_TIMEZONES.filter(isTimezone);
    const rest = supported.filter((zone) => !COMMON_TIMEZONES.includes(zone));
    return [...common, ...rest];
}
/* — days — */
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;
/** How the shop writes the days of the week and the months.
 *
 *  Hand-kept rather than taken from `Intl`, and deliberately: `en-AU` renders
 *  "Fri, 11 Sept" and the shop writes "Fri 11 Sep" — on the board, on the job
 *  sheet, and on the docket that goes out with the work. The format is the
 *  shop's, so it is not the browser's to change under it. */
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];
/** Is this a day, and a real one? Rejects "2026-02-31" as well as nonsense —
 *  a date field that let a 31st of February through would put a job on a day
 *  that does not exist. */
export function isDay(value) {
    if (typeof value !== 'string')
        return false;
    const parts = ISO_DAY.exec(value);
    if (!parts)
        return false;
    const at = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(at.getTime()) && at.toISOString().slice(0, 10) === value;
}
/** A day as the `Date` a `date` column is read and written as: UTC midnight.
 *
 *  Every `Date` this module makes is one of these. It is a calendar position,
 *  not a moment — do not read an hour off it. */
export function dayAsDate(day) {
    return new Date(`${day}T00:00:00.000Z`);
}
/** A `date` column back as the day it is.
 *
 *  Not `toISOString().slice(0, 10)` by luck: Postgres hands a `date` back at
 *  UTC midnight, so cutting the string at the day is exactly right, and
 *  anything that goes through a local timezone is exactly wrong. */
export function dayOfDate(at) {
    return at.toISOString().slice(0, 10);
}
/** `n` days after a day. Negative goes back. */
export function addDays(day, n) {
    return dayOfDate(new Date(dayAsDate(day).getTime() + n * DAY_MS));
}
/** How many days from one day to the next. Negative when `to` is earlier.
 *
 *  Whole days, always: both ends are UTC midnight, so there is no daylight
 *  saving in here to make a day 23 hours long. */
export function daysBetween(from, to) {
    return Math.round((dayAsDate(to).getTime() - dayAsDate(from).getTime()) / DAY_MS);
}
/** Earlier, same, later — the comparator every sort over days uses.
 *
 *  Days sort correctly as strings, which is half the reason they are strings;
 *  this exists so a caller says what it means rather than leaving the next
 *  reader to work out whether `a < b` on two strings was deliberate. */
export function compareDays(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
/** The day of the week, Sun 0 … Sat 6. */
export function weekdayOf(day) {
    return dayAsDate(day).getUTCDay();
}
/** Nobody is spraying. */
export function isWeekend(day) {
    const at = weekdayOf(day);
    return at === 0 || at === 6;
}
/** The Monday on or before a day. The shop counts a week from Monday, because
 *  that is when the truck comes. */
export function mondayOf(day) {
    return addDays(day, -((weekdayOf(day) + 6) % 7));
}
/** The first working day on or after a day. Est. days are working days, so a
 *  plan that starts on a Saturday starts on a day nobody is in. */
export function nextWorkday(day) {
    let at = day;
    while (isWeekend(at))
        at = addDays(at, 1);
    return at;
}
/** The last day of a run of `days` working days beginning on `day`.
 *
 *  Est. days are working days, so four days from a Thursday lands on the
 *  Tuesday, not the Sunday — and the racks are occupied over the weekend in
 *  between. The first day counts as one. */
export function workdaysFrom(day, days) {
    let at = nextWorkday(day);
    for (let left = Math.max(1, days) - 1; left > 0; left -= 1)
        at = nextWorkday(addDays(at, 1));
    return at;
}
/* — instants, and what "now" is —
 *
 * The only two questions a timezone is needed for. */
/** The wall-clock parts of an instant, in a given timezone.
 *
 *  `formatToParts` rather than arithmetic on an offset, because an offset is
 *  not a constant: it moves with daylight saving, and Brisbane's neighbours
 *  observe it. This is the one place the shift happens. */
function partsIn(at, timezone) {
    const parts = new Intl.DateTimeFormat('en-AU', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(at);
    const found = {};
    for (const part of parts)
        found[part.type] = part.value;
    return found;
}
/** A timezone that will not throw, so a mistyped one in Settings costs an hour
 *  of accuracy on one screen rather than the screen. */
const usable = (timezone) => (isTimezone(timezone) ? timezone : 'UTC');
/** What day it is where the shop is.
 *
 *  Not the container's day and not the tablet's: the server runs in UTC and
 *  would have Brisbane on yesterday until 10am, and an iPad somebody carried
 *  back from Perth is two hours behind the board. The shop's timezone is a
 *  column on the organisation precisely so this question has one answer. */
export function todayIn(timezone, now = new Date()) {
    const at = partsIn(now, usable(timezone));
    return `${at['year']}-${at['month']}-${at['day']}`;
}
/** The instant an ISO string names, or null for anything unreadable — never
 *  an `Invalid Date` handed to a caller. */
export function instantOf(value) {
    if (!value)
        return null;
    const at = new Date(value);
    return Number.isNaN(at.getTime()) ? null : at;
}
/* — how a date is written —
 *
 * Presentation, in one place. A day has no timezone, so formatting one needs
 * none; an instant is rendered in the shop's. */
/** A day as the shop writes it — "Thu 3 Sep", or "Thu 3 Sep 2026".
 *
 *  The year is off by default because the board and the schedule read a
 *  fortnight at a time and it is noise there. A date *field* is where somebody
 *  is committing to a day, so that says which year they are committing to. */
export function formatDay(day, opts = {}) {
    if (!isDay(day))
        return NOT_SET;
    const at = dayAsDate(day);
    const written = `${DOW[at.getUTCDay()]} ${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]}`;
    return opts.year ? `${written} ${at.getUTCFullYear()}` : written;
}
/** Just the weekday — "Thu". What a schedule column header and a chart axis
 *  have room for, and the same three letters `formatDay` writes. */
export function formatWeekday(day) {
    return isDay(day) ? DOW[dayAsDate(day).getUTCDay()] : '';
}
/** A day without its weekday — "3 Sep". What a chart axis and a dense column
 *  have room for. */
export function formatDayShort(day, opts = {}) {
    if (!isDay(day))
        return NOT_SET;
    const at = dayAsDate(day);
    const written = `${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]}`;
    return opts.year ? `${written} ${at.getUTCFullYear()}` : written;
}
/** An instant as the shop reads it, in the shop's own timezone —
 *  "3 Sep, 2:14 pm", or with the year where it could be any year.
 *
 *  Rendered rather than stored this way: the column is UTC, and this is the
 *  only thing that turns it into a wall clock. */
export function formatInstant(value, timezone, opts = {}) {
    const at = instantOf(value);
    if (at === null)
        return NOT_SET;
    const on = partsIn(at, usable(timezone));
    const day = `${on['year']}-${on['month']}-${on['day']}`;
    const hour24 = Number(on['hour']);
    const hour = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const clock = `${hour}:${on['minute']} ${hour24 < 12 ? 'am' : 'pm'}`;
    return `${formatDayShort(day, opts)}, ${clock}`;
}
/** The day an instant fell on where the shop is. What a notice board reads by,
 *  and how an instant is compared against a day. */
export function dayOfInstant(value, timezone) {
    const at = instantOf(value);
    return at === null ? null : todayIn(timezone, at);
}
/** What a screen draws for a day nobody has set. A real state, not an error: a
 *  job can be taken in before the builder has confirmed handover. */
export const NOT_SET = 'Not set';
//# sourceMappingURL=time.js.map